import { Layout } from 'components/Layout/Layout';
import styles from '../styles/pages/MyBets.module.css';
import { useTranslation } from 'react-i18next';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useListScrollRestore } from 'hooks/useListScrollRestore';
import classNames from 'classnames';
import { SlipStatus } from 'types/enums';
import useAsyncEffect from 'use-async-effect';
import { getSlips } from 'services/api/slipService';
import { UserContext } from 'context/userContext';
import { Loader } from 'components/Loader/Loader';
import { SlipEvents } from 'components/SlipEvents/SlipEvents';
import { Slip } from 'types/Slip';
import { sortArrayByDate } from 'helpers/dateHelper';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { SlipEventsMobile } from 'components/mobile/SlipEventsMobile/SlipEventsMobile';
import { SlipDetails } from 'components/mobile/SlipDetails/SlipDetails';
import 'react-datepicker/dist/react-datepicker.css';
import { ClosedBetsFilter as ClosedBetsFilterMobile } from 'components/mobile/ClosedBetsFilter/ClosedBetsFilter';
import { ClosedBetsFilter } from 'components/ClosedBetsFilter/ClosedBetsFilter';
import { CLOSED_BETS_DEFAULT_DAYS } from 'config/constants';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { useNavigate } from 'react-router-dom';
import { useCashoutOffers } from 'hooks/useCashoutOffers';

export const MyBets = () => {
  const [betStatus, setBetStatus] = useState<
    SlipStatus.OPEN | SlipStatus.CLOSED
  >(SlipStatus.OPEN);
  const [isLoading, setIsLoading] = useState(true);
  const [slips, setSlips] = useState<Slip[]>([]);
  const [showSlipDetails, setShowSlipDetailsState] = useState<Slip | null>(
    null
  );
  // Mobile swaps the list for the ticket's details. Coming back puts the list
  // where it was and marks the ticket just left, instead of the top.
  const listScroll = useListScrollRestore(showSlipDetails != null);
  const [lastViewedSlipId, setLastViewedSlipId] = useState<number | null>(null);
  const setShowSlipDetails: Dispatch<SetStateAction<Slip | null>> = (next) => {
    const slip = typeof next === 'function' ? next(showSlipDetails) : next;
    if (slip && !showSlipDetails) {
      listScroll.remember();
      setLastViewedSlipId(slip.id);
    }
    setShowSlipDetailsState(slip);
  };
  const { loggedUser } = useContext(UserContext);
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const [reloadKey, setReloadKey] = useState(0);
  const cashout = useCashoutOffers(
    !!loggedUser && betStatus === SlipStatus.OPEN
  );
  // A cashed out ticket leaves the open list.
  const onCashedOut = () => {
    setShowSlipDetails(null);
    setReloadKey((k) => k + 1);
    cashout.refresh();
  };

  // Open tickets change on their own (a goal, a settled pick, a win). While
  // the open list is on screen it is reloaded quietly: at once when a ticket
  // drops out of the cash out offers (it closed), otherwise every 15 s. A
  // ticket shown in detail is swapped for its new version, from the closed
  // list once it has left the open one.
  const slipsRef = useRef<Slip[]>([]);
  slipsRef.current = slips;
  const detailRef = useRef<Slip | null>(null);
  detailRef.current = showSlipDetails;
  const statusRef = useRef(betStatus);
  statusRef.current = betStatus;
  const refreshing = useRef(false);
  const refreshOpen = async () => {
    if (!loggedUser || refreshing.current) return;
    if (document.visibilityState === 'hidden') return;
    refreshing.current = true;
    try {
      const result = await getSlips(loggedUser, SlipStatus.OPEN);
      if (!result || statusRef.current !== SlipStatus.OPEN) return;
      setSlips(result);
      const shown = detailRef.current;
      if (!shown) return;
      let fresh = result.find((s) => s.id === shown.id);
      if (!fresh) {
        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        startDate.setDate(startDate.getDate() - CLOSED_BETS_DEFAULT_DAYS);
        const closed = await getSlips(loggedUser, SlipStatus.CLOSED, startDate);
        fresh = closed?.find((s) => s.id === shown.id);
      }
      if (fresh && detailRef.current?.id === shown.id) {
        setShowSlipDetailsState(fresh);
      }
    } finally {
      refreshing.current = false;
    }
  };
  const refreshOpenRef = useRef(refreshOpen);
  refreshOpenRef.current = refreshOpen;

  useEffect(() => {
    if (!loggedUser || betStatus !== SlipStatus.OPEN) return;
    const id = window.setInterval(() => refreshOpenRef.current(), 15_000);
    return () => window.clearInterval(id);
  }, [loggedUser, betStatus]);

  useEffect(() => {
    if (!cashout.enabled || statusRef.current !== SlipStatus.OPEN) return;
    const open = slipsRef.current;
    // The offers cover at most 100 open tickets.
    if (
      open.length > 0 &&
      open.length <= 100 &&
      open.some((s) => !cashout.offers.has(s.id))
    ) {
      refreshOpenRef.current();
    }
  }, [cashout.offers, cashout.enabled]);

  useAsyncEffect(async () => {
    if (loggedUser) {
      setIsLoading(true);
      // Closed bets: the last 7 days, as the filter shows pre-selected.
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      startDate.setDate(startDate.getDate() - CLOSED_BETS_DEFAULT_DAYS);
      const result = await getSlips(
        loggedUser,
        betStatus,
        betStatus !== SlipStatus.OPEN ? startDate : undefined
      );
      if (result) {
        setSlips(result);
      } else {
        setSlips([]);
      }
      setIsLoading(false);
    }
  }, [loggedUser, betStatus, reloadKey]);

  if (!loggedUser) {
    navigate('/login');
  }

  // The filter reloads on every change, so answers can overtake each other;
  // only the newest request's list is shown.
  const latestFilter = useRef(0);
  const filterClosedSlips = async (
    startDate: Date,
    endDate: Date,
    betStatus: SlipStatus,
    onlyLiveBets?: boolean
  ) => {
    const request = ++latestFilter.current;
    setIsLoading(true);
    const result = await getSlips(
      loggedUser,
      betStatus,
      startDate,
      endDate,
      onlyLiveBets
    );
    if (request !== latestFilter.current) return;

    if (result) {
      setSlips(sortArrayByDate(result, 'created_at', 'DESC'));
    } else {
      setSlips([]);
    }
    setIsLoading(false);
  };

  return (
    <>
      {isMobile ? (
        <MobileLayout pageTitle={t('my_bets_label')}>
          {showSlipDetails ? (
            <SlipDetails
              slip={showSlipDetails}
              setShowSlipDetails={setShowSlipDetails}
              cashout={cashout.offers.get(showSlipDetails.id)}
              onCashedOut={onCashedOut}
            />
          ) : (
            <>
              <BetTypeSelector
                betStatus={betStatus}
                setBetStatus={setBetStatus}
              />
              {betStatus === SlipStatus.CLOSED && (
                <ClosedBetsFilterMobile getSlips={filterClosedSlips} />
              )}
              {slips.length > 0 ? (
                <>
                  <div className={styles.numberOfSlips}>
                    <ThemeIcon icon="my-bets" alt="My Bets" variant="inline" />
                    {t('number_of_bet_slips_label')}
                    <div>{slips.length}</div>
                  </div>
                  <div className={styles.slipsWrapper}>
                    {isLoading && <Loader />}
                    {!isLoading &&
                      slips
                        .sort((a, b) => b.id - a.id)
                        .map((slip: Slip) => (
                          <SlipEventsMobile
                            slip={slip}
                            setShowSlipDetails={setShowSlipDetails}
                            highlighted={slip.id === lastViewedSlipId}
                            cashout={cashout.offers.get(slip.id)}
                            onCashedOut={onCashedOut}
                            key={slip.id}
                          />
                        ))}
                  </div>
                </>
              ) : (
                <NoResultsMessage betStatus={betStatus} mobile />
              )}
            </>
          )}
        </MobileLayout>
      ) : (
        <Layout>
          <div className={styles.content}>
            <span>{t('my_bets_label')}</span>
            <BetTypeSelector
              betStatus={betStatus}
              setBetStatus={setBetStatus}
            />
            {betStatus === SlipStatus.CLOSED && (
              <ClosedBetsFilter getSlips={filterClosedSlips} />
            )}
            <div className={styles.slipsWrapper}>
              {isLoading && <Loader />}
              {!isLoading && slips.length > 0 ? (
                slips
                  .sort((a, b) => b.id - a.id)
                  .map((slip: Slip) => (
                    <SlipEvents
                      slip={slip}
                      cashout={cashout.offers.get(slip.id)}
                      onCashedOut={onCashedOut}
                      key={slip.id}
                    />
                  ))
              ) : (
                <NoResultsMessage betStatus={betStatus} />
              )}
            </div>
          </div>
        </Layout>
      )}
    </>
  );
};

interface Props {
  betStatus: SlipStatus.OPEN | SlipStatus.CLOSED;
  setBetStatus: Dispatch<SetStateAction<SlipStatus.OPEN | SlipStatus.CLOSED>>;
}

const BetTypeSelector = ({ betStatus, setBetStatus }: Props) => {
  const { t } = useTranslation();

  return (
    <div className={styles.betTypeWrapper}>
      <div
        className={classNames({
          [styles['selectedBetType']]: betStatus === SlipStatus.OPEN,
        })}
        onClick={() => setBetStatus(SlipStatus.OPEN)}
      >
        <div
          className={classNames('checkmark-circle-icon-left', 'desktop-hidden')}
        />
        {t('open_bets_label')}
      </div>
      <div
        className={classNames({
          [styles['selectedBetType']]: betStatus === SlipStatus.CLOSED,
        })}
        onClick={() => setBetStatus(SlipStatus.CLOSED)}
      >
        <div
          className={classNames(
            'checkmark-circle-icon-left',
            'desktop-hidden',
            styles.positionRight
          )}
        />
        {t('closed_bets_label')}
      </div>
    </div>
  );
};

interface NoResultsMessageProps {
  betStatus: SlipStatus.OPEN | SlipStatus.CLOSED;
  mobile?: boolean;
}

const NoResultsMessage = ({
  betStatus = SlipStatus.OPEN,
  mobile = false,
}: NoResultsMessageProps) => {
  const { t } = useTranslation();
  return (
    <div className={styles.noResultsWrapper}>
      <ThemeIcon
        icon={mobile ? 'info-circle' : 'info-full'}
        alt="Info"
        variant="inline"
      />
      <span>
        {t(
          betStatus === SlipStatus.OPEN
            ? 'no_open_bets_message'
            : 'no_closed_bets_message'
        )}
      </span>
    </div>
  );
};
